import { moneyHint } from '@/lib/money';

export interface SignedMoney {
  /** Currency-aware display string with an explicit +/− sign, e.g. "−฿120.50". */
  text: string;
  /** True when the underlying minor-unit amount is below zero (node owes). */
  negative: boolean;
  /** True when the amount is exactly zero (no credit moved this period). */
  zero: boolean;
}

/**
 * Format a SIGNED minor-unit money string (as emitted by svc-commission — a
 * decimal string of integer minor units, possibly prefixed `-`) for display.
 *
 * creditDelta is signed: a negative value means the node net-lost and was
 * DEBITED (it owes), a positive value means it was credited. The sign must be
 * unmistakable, so a positive non-zero amount carries an explicit `+`. Parsing
 * and formatting are currency-aware (THB 2dp, JPY/KRW 0dp, Gulf dinars 3dp) via
 * `fromMinor` — never a `Number(x) / 100` cast, which corrupts non-2dp money.
 */
export function signedMoney(minor: string | number | null | undefined, currency = 'THB'): SignedMoney {
  if (minor === null || minor === undefined || minor === '') {
    return { text: '-', negative: false, zero: true };
  }
  const text = String(minor).trim();
  const negative = text.startsWith('-');
  const magnitude = negative ? text.slice(1) : text;

  // Zero detection works on the raw minor-unit digits, not the formatted
  // string: any all-zero magnitude ("0", "000", "") is no movement, so it
  // carries neither a sign nor a sign-coloured tone.
  const digits = magnitude.replace(/[^0-9]/g, '');
  const zero = digits === '' || /^0+$/.test(digits);

  const hint = moneyHint(magnitude, currency) || moneyHint('0', currency) || '-';
  if (zero) return { text: hint, negative: false, zero: true };

  return {
    text: negative ? `−${hint}` : `+${hint}`,
    negative,
    zero: false,
  };
}
